import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, Box, CircularProgress, MenuItem, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from '@mui/material';
import api from '../../api';

const AttendanceReportSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const currentDate = new Date();
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());
  
  const empId = localStorage.getItem('emp_id');

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard/report/${empId}?month=${month}&year=${year}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, [empId, month, year]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress size={24} /></Box>;
  if (!data) return <Typography color="error" variant="body2">Failed to load data.</Typography>;

  const KPICard = ({ title, value, subtitle, highlightColor = '#1a1a1a' }) => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#e0e0e0', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>{title}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: highlightColor }}>{value}</Typography>
      {subtitle && <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>{subtitle}</Typography>}
    </Paper>
  );

  const getStatusColor = (status) => {
    const s = status.toUpperCase();
    if (s.includes('PRESENT')) return { bg: '#e8f5e9', color: '#2e7d32' };
    if (s === 'WO') return { bg: '#eceff1', color: '#546e7a' };
    if (s === 'DLOP') return { bg: '#ffebee', color: '#c62828' };
    if (s === 'LOP') return { bg: '#fce4ec', color: '#c2185b' };
    if (s === 'HALF-DAY') return { bg: '#fff3e0', color: '#e65100' };
    if (['CL', 'ML', 'AL', 'NH', 'EL'].includes(s)) return { bg: '#e3f2fd', color: '#1565c0' };
    if (s === 'UPCOMING') return { bg: '#ffffff', color: '#9e9e9e', border: '1px dashed #9e9e9e' };
    return { bg: '#f5f5f5', color: '#757575' }; // UNALLOCATED
  };

  const LegendItem = ({ label, colorCode }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: colorCode }} />
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#424242' }}>{label}</Typography>
    </Box>
  );

  return (
    <Box>
      {/* Header & Filters */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a1a' }}>Detailed Attendance Report</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{data.period_str}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField select size="small" value={month} onChange={(e) => setMonth(e.target.value)} sx={{ width: 120, bgcolor: 'white' }}>
            {[...Array(12)].map((_, i) => (<MenuItem key={i+1} value={i+1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</MenuItem>))}
          </TextField>
          <TextField select size="small" value={year} onChange={(e) => setYear(e.target.value)} sx={{ width: 100, bgcolor: 'white' }}>
            {[year-1, year, year+1].map(y => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
          </TextField>
        </Box>
      </Box>

      {/* KPI Row 1 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}><KPICard title="Present" value={data.stats.present} subtitle="Includes Present & Grace" highlightColor="#2e7d32" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KPICard title="Weekly Off (WO)" value={data.stats.wo} subtitle="Scheduled Weekly Offs" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KPICard title="All Leaves" value={data.stats.leaves} subtitle="CL, ML, AL, NH, EL" highlightColor="#1565c0" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KPICard title="Unallocated" value={data.stats.unallocated} subtitle="Days with no shift" color="textSecondary" /></Grid>
      </Grid>

      {/* KPI Row 2 */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}><KPICard title="Half Day" value={data.stats.half_day} subtitle="Deducts 0.5 days" highlightColor="#e65100" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KPICard title="LOP" value={data.stats.lop} subtitle="Deducts 1 day" highlightColor="#c2185b" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KPICard title="DLOP" value={data.stats.dlop} subtitle="Deducts 2 days" highlightColor="#c62828" /></Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#3f51b5', bgcolor: '#e8eaf6', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: '#3f51b5', mb: 0.5 }}>Pay Days</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e' }}>{data.stats.pay_days} / {data.total_cycle_days}</Typography>
            <Typography variant="caption" sx={{ color: '#5c6bc0', mt: 0.5 }}>Formula: {data.total_cycle_days} - Unallocated - HalfDay - LOP - (2x DLOP)</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Table Section */}
      <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: '#e0e0e0', overflow: 'hidden' }}>
        
        {/* Table Legend */}
        <Box sx={{ p: 2, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0', display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <LegendItem label="PRESENT" colorCode="#2e7d32" />
          <LegendItem label="DLOP" colorCode="#c62828" />
          <LegendItem label="WO" colorCode="#546e7a" />
          <LegendItem label="HALF-DAY" colorCode="#e65100" />
          <LegendItem label="UNALLOCATED" colorCode="#9e9e9e" />
          <LegendItem label="LEAVE" colorCode="#1565c0" />
          <LegendItem label="LOP" colorCode="#c2185b" />
        </Box>

        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: '#424242' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: '#424242' }}>Shift</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: '#424242' }}>Check-In</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: '#424242' }}>Check-Out</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: '#424242' }}>Working Hrs</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: '#424242' }}>Office Hrs</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: '#424242', textAlign: 'center' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.daily_data.map((row, idx) => {
                const style = getStatusColor(row.status);
                return (
                  <TableRow key={idx} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{row.date}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{row.shift}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.in_time}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.out_time}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.worked_hrs}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.office_hrs}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={row.status} 
                        size="small" 
                        sx={{ 
                          bgcolor: style.bg, 
                          color: style.color, 
                          fontWeight: 700, 
                          fontSize: '0.7rem', 
                          borderRadius: 1, 
                          height: 24,
                          border: style.border || 'none'
                        }} 
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AttendanceReportSection;